import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useAppearance } from '@traceio-org/auth-ui';
import { hubUrl } from '@traceio-org/platform-contracts';
import { NavigationShell } from '@traceio-org/ui';
import { useAbilities, useAdmin, useStaff } from '../context';
import {
  KeysSummaryProvider,
  keysAttentionWords,
  keysNeedingAttention,
  useKeysSummary,
} from '../keysSummary';
import { STAFF_ROLE_LABELS, initialsOf } from '../format';
import { useTicketInboxCount } from '../ticketsInbox';
import { ADMIN_BRAND, ADMIN_PRODUCT_NAME, adminNavigation, sectionForPath, sectionIcon } from '../navigation';
import {
  APPEARANCE_ITEM_ID,
  AppearanceMenu,
  appearanceItem,
  useAppearanceTriggerState,
} from './AppearanceMenu';

/**
 * Who is signed in, from GET /v1/admin/me (through the gate): name and staff role, a link to the account hub's
 * Account & billing pages, and Sign out, which ends the session at the account hub (/logout?product=admin). On narrow screens the name and role are visually
 * hidden but stay in the accessible name; the initials remain.
 */
export function StaffIdentity() {
  const { person, context } = useStaff();
  const { hubOrigin } = useAdmin();
  const role = STAFF_ROLE_LABELS[context.staffRole];
  return (
    <div className="admin-staff" role="group" aria-label="Staff account">
      <span className="admin-staff__avatar" aria-hidden="true" title={`${person.displayName}, ${role}`}>
        {initialsOf(person.displayName)}
      </span>
      <p className="admin-staff__who">
        <span className="admin-sr-only">Signed in as </span>
        <span className="admin-staff__name">{person.displayName}</span>
        <span className="admin-sr-only">, </span>
        <span className="admin-staff__role">{role}</span>
      </p>
      {/* The account hub's own pages for the staff member's organization (members, billing); the hub signs them
          in silently when the Auth0 session already exists. hubUrl has no settings route, so the path is joined here. */}
      <a className="admin-staff__signout" href={new URL('/settings', hubOrigin).toString()}>
        Account &amp; billing
      </a>
      <a className="admin-staff__signout" href={hubUrl(hubOrigin, { route: 'logout', productKey: 'admin' })}>
        Sign out
      </a>
    </div>
  );
}

/**
 * The staff shell: the library's NavigationShell with Trace Admin's identity and the navigation this staff
 * member's permissions show (Billing only with staff.billing.read), the current area as the top bar title and
 * the staff identity in the top bar, with Keys' count of keys expired or due (KeysSummaryProvider) as a red badge.
 * Navigation clicks go through the router (no page reload, so the confirmed
 * session and access stay in memory); modified clicks keep the browser's behavior, as the shell implements. The
 * Appearance row opens the appearance menu instead of navigating.
 */
export function AdminLayout() {
  return (
    <KeysSummaryProvider>
      <AdminShell />
    </KeysSummaryProvider>
  );
}

/**
 * The Keys link's accessible name and tooltip in the words of its badge ("Keys, 2 keys expired or due"). Risky, so
 * spelled out: trace-ui's NavigationShell (vendored, e5ce5602ae) names a badge "Keys, 2 items" and has no prop for the
 * words, so this sets aria-label and title on its Keys link after each render of the navigation (the rail and the
 * expanded list both), and only when they differ, so the observer never loops. Remove once trace-ui takes a badge label.
 */
function useKeysBadgeWords(count: number): void {
  useEffect(() => {
    if (count <= 0 || typeof MutationObserver === 'undefined') return;
    const label = `Keys, ${keysAttentionWords(count)}`;
    const apply = () => {
      for (const link of document.querySelectorAll<HTMLElement>('.trace-nav-link[href="/keys"]')) {
        if (link.getAttribute('aria-label') !== label) link.setAttribute('aria-label', label);
        if (link.getAttribute('title') !== label) link.setAttribute('title', label);
      }
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-label', 'title'],
    });
    return () => observer.disconnect();
  }, [count]);
}

function AdminShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const abilities = useAbilities();
  const keysAttention = keysNeedingAttention(useKeysSummary().summary);
  useKeysBadgeWords(keysAttention);
  const inbox = useTicketInboxCount(abilities.readTickets);
  const groups = useMemo(
    () => adminNavigation(abilities, { keys: keysAttention, tickets: inbox }),
    [abilities, keysAttention, inbox],
  );
  const section = sectionForPath(location.pathname);
  const { hubOrigin } = useAdmin();
  // Shared with the account hub through the cookie on the parent domain (auth-ui appearance).
  const { preference, shared, setPreference } = useAppearance(hubOrigin);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const closeAppearance = useCallback(() => setAppearanceOpen(false), []);
  useAppearanceTriggerState(appearanceOpen);
  return (
    <>
      <NavigationShell
        brand={ADMIN_BRAND}
        groups={groups}
        activeId={section?.id ?? ''}
        title={section?.label ?? ADMIN_PRODUCT_NAME}
        {...(section ? { titleIcon: sectionIcon(section) } : {})}
        topbarActions={<StaffIdentity />}
        appearanceItem={appearanceItem(preference)}
        onNavigate={(item) =>
          item.id === APPEARANCE_ITEM_ID ? setAppearanceOpen((open) => !open) : navigate(item.href)
        }
        searchLabel="Search Trace Admin pages"
        persistenceKey="trace-admin.navigation.collapsed"
      >
        <Outlet />
      </NavigationShell>
      {appearanceOpen ? (
        <AppearanceMenu
          preference={preference}
          shared={shared}
          onChange={setPreference}
          onClose={closeAppearance}
        />
      ) : null}
    </>
  );
}
